import {t} from '@lingui/macro'
import React from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {v4 as uuid} from 'uuid'

import config from 'config'
import {isJwt} from '~/auth/jwt'
import {useRegisterState} from '~/feature-register/use-register-state'
import {showAlertModal} from '~/generic-ui/alert-modal/alert'
import type {SupportedLanguage} from '~/i18n'
import {usePrevious} from '~/react-utils'
import * as tracking from '~/tracking'
import {sessionStorage} from '~/web-platform/storage/session'

import {forceSessionRefresh, useAccessToken} from './session'
import {useToaster} from './toaster'

type OauthContextAPI = {oauthId: string}

const OAUTH_ID_STORAGE_KEY = 'oauthId'

const OAUTH_ADD_ACCOUNT = 'oauthAddAccount'

////////////////////////////////////////////////////////////////////////////////
// #region Oauth Context

export const OauthContext = React.createContext<OauthContextAPI>({
    oauthId: 'test-oauth-id',
})

export const OauthProvider: React.FC = ({children}) => {
    const accessToken = useAccessToken()
    // Initialize oauth ID with either the value saved in session storage
    // or a newly generated oauth ID
    const [oauthId, setOauthId] = React.useState(
        () => sessionStorage.getItem(OAUTH_ID_STORAGE_KEY) ?? getNewOauthId(),
    )

    const previousAccessToken = usePrevious(accessToken)
    React.useEffect(() => {
        // If previous access token is not defined, it's the first render and
        // we should not try to regenerate the oauth token
        if (previousAccessToken === undefined || !isJwt(previousAccessToken ?? '')) {
            return
        }

        // If the token changed, it means that the user changed, logged out,
        // etc. and we should generate a new oauth ID
        setOauthId(getNewOauthId())
    }, [accessToken])

    return <OauthContext.Provider value={{oauthId}}>{children}</OauthContext.Provider>
}

// Generates a new oauth id, saves it in session storage so it can be retrieved
// after the full page refresh (e.g. oauth redirect)
function getNewOauthId() {
    const newOauthId = uuid()
    sessionStorage.setItem(OAUTH_ID_STORAGE_KEY, newOauthId)
    return newOauthId
}

// #endregion OauthContext Context
////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////
// #region OAuth URL generation
/**
 * Custom React hook retrieving the oauth ID used for registration, verification
 * and login flows
 * @returns Currently saved Oauth ID
 */
export function useOauthId() {
    const {oauthId} = React.useContext(OauthContext)
    return oauthId
}

export enum ExternalProvider {
    GOOGLE = 'google',
    MICROSOFT = 'microsoft',
}

type OauthRegisterAction = {
    action: 'register'
    state?: {
        inviteToken?: string
    }
}
type OauthVerifyAction = {
    action: 'verify'
    state: {
        userId: string
        verifyToken: string
    }
}
type OauthOtherAction = {
    action: 'login' | 'external'
    state?: {
        language?: SupportedLanguage
    }
}
type OauthExternalAction = {
    action: 'external'
    provider: ExternalProvider
    register?: boolean | string
    verify?: boolean
    responseType?: 'code' | 'token'
    codeChallenge?: string
    codeChallengeMethod?: 'S256' | 'plain'
}
type OauthExternalRegisterAction = OauthExternalAction & {
    register: true
    state?: {
        language?: SupportedLanguage
        inviteToken?: string
    }
}
type OauthExternalVerifyAction = OauthExternalAction & {
    verify: true
    state: {
        language?: SupportedLanguage
        verifyToken: string
    }
}
type OauthExternalLoginAction = OauthExternalAction & {
    register: undefined
    verify: undefined
}
type OauthAction =
    | OauthRegisterAction
    | OauthVerifyAction
    | OauthExternalVerifyAction
    | OauthOtherAction
    | OauthExternalRegisterAction
    | OauthExternalLoginAction
export type OauthHookParams = OauthAction & {
    scope: string[]
    redirectUri?: string
    state?: {}
}

const isExternalAction = (action: OauthAction): action is OauthExternalAction =>
    (action as OauthExternalAction).action === 'external' &&
    (action as OauthExternalAction).provider !== undefined

const isExternalRegisterAction = (action: OauthAction): action is OauthExternalRegisterAction =>
    isExternalAction(action) && (action as OauthExternalRegisterAction).register

const isExternalVerifyAction = (action: OauthAction): action is OauthExternalVerifyAction =>
    isExternalAction(action) &&
    (action as OauthExternalVerifyAction).verify &&
    (action as OauthExternalVerifyAction).state?.verifyToken !== undefined

/**
 * Custom React hook retrieving the full URL for Oauth form submission
 * @param params.action Determines which oauth action the form submission will perform
 * @param params.state Additional data, depends on the action chosen
 * @param params.scope Authentication scopes (e.g. ['resource:basic'])
 * @param params.provider Selects a 3rd party authentication provider if needed
 * @param params.register Used in tandem with provider to determine if user should be registered if not yet the case
 * @param params.redirectUri Overrides the default redirect URL (current page)
 * @param params.responseType Determines if the response should be in the form of a code token (preferred) or an access token.
 * @param params.codeChallenge Used when response_type is set to 'code'. A base 64 url encoded high-entropy cryptographic random STRING using the unreserved characters [A-Z] / [a-z] / [0-9] / "-" / "." / "_" / "~" from Section 2.3 of [RFC3986], with a minimum length of 43 characters and a maximum length of 128 characters.
 * @param params.codeChallengeMethod Either 'plain' or 'S256'. If set to 'S256', the code_challenge needs to be hashed using S256 before base 64 encoding it.
 * @returns The Oauth form submission URL
 */
export function useOauthUrl(params: OauthHookParams) {
    const oauthId = useOauthId()

    const {protocol, host, pathname} = window.location
    const defaultRedirectUri = `${protocol}//${host}${pathname}`
    const baseUrl = config.endpoints.auth
    const url = new URL(`oauth/${params.action}`, baseUrl)

    // Shared params for all oauth calls
    url.searchParams.append('client_id', config.auth.oauthClientId)
    url.searchParams.append('scope', params.scope.join(','))
    url.searchParams.append('state', JSON.stringify({...params.state, id: oauthId}))
    url.searchParams.append('redirect_uri', params.redirectUri ?? defaultRedirectUri)

    // Shared params for all external provider oauth calls
    if (isExternalAction(params)) {
        url.searchParams.append('provider', params.provider)

        if (params.responseType) {
            url.searchParams.append('response_type', params.responseType)
        }

        if (params.codeChallenge) {
            url.searchParams.append('code_challenge', params.codeChallenge)
        }

        if (params.codeChallengeMethod) {
            url.searchParams.append('code_challenge_method', params.codeChallengeMethod)
        }
    }

    // Action specific call params
    if (params.action === 'login') {
        url.searchParams.append('response_type', 'token')
    }

    if (isExternalVerifyAction(params)) {
        url.searchParams.append('verify', 'true')
    }

    if (isExternalRegisterAction(params)) {
        url.searchParams.append('register', 'sca')
    }

    return url.toString()
}

// #endregion OAuth URL generation
////////////////////////////////////////////////////////////////////////////////

////////////////////////////////////////////////////////////////////////////////
// #region OAuth URL parsing

/**
 * Parses the oauth callback params from the URL's hash part, if available
 * @returns Oauth callback params if available
 */
export function getOAuthCallbackParams(hash: string) {
    if (!hash) {
        return null
    }
    const params = new URLSearchParams(hash.slice(1))

    // If both error and message are set in hash, it's most likely a failed
    // oauth attempt (like invalid password)
    const error = params.get('error')
    const message = params.get('message')
    if (error && message) {
        return {error: true, message} as const
    }

    // if `email` is set in hash, it means that we deal with google signup which return `email`, `firstName` and `lastName`
    const email = params.get('email')
    const firstName = params.get('firstName')
    const lastName = params.get('lastName')
    const loginToken = params.get('login_token')
    const idToken = params.get('id_token')

    if (email) {
        return {
            email,
            loginToken,
            firstName,
            lastName,
            idToken,
        }
    }

    // If both access_token and state are set in hash, it's most likely
    // a successful oauth attempt. Note that state will be serialized JSON
    // so we try to parse it here as well.
    const accessToken = params.get('access_token')
    const rawState = params.get('state')

    if (accessToken && rawState) {
        try {
            const state = JSON.parse(rawState) ?? {}

            // As a level of protection, we expect the `state.id` from the oauth callback
            // params to equal the oauthId generated on the client when triggering the oauth flow
            // This ensures that we only act on callbacks that were triggered by the same browser
            const oauthId = sessionStorage.getItem(OAUTH_ID_STORAGE_KEY)

            if (oauthId !== state.id) {
                throw new Error('Mismatched oauthId')
            }

            return {
                accessToken,
                state,
            }
        } catch (e) {
            return {
                error: true,
                message: 'invalid_state',
            } as const
        }
    }

    return null
}

/**
 * Custom hook that reacts to errors returned in Oauth callbacks and clears the URL.
 * Should be used only once in the app in the top level container component
 */
export function useHandleOauthCallback() {
    const {showToast} = useToaster()
    const navigate = useNavigate()
    const location = useLocation()
    const [params, setParams] = useRegisterState()
    const oauthParams = getOAuthCallbackParams(location.hash)

    React.useEffect(() => {
        if (oauthParams?.error && oauthParams?.message) {
            switch (oauthParams.message) {
                case 'user_not_active':
                    showAlertModal({
                        title: t`Account disabled`,
                        message: t`Your account is disabled. Please contact support.`,
                    })
                    break
                case 'throttled':
                    showAlertModal({
                        title: t`Locked`,
                        message: t`Too many wrong password attempts.`,
                    })
                    break
                case 'exists':
                    showToast(t`User already exists`, {
                        level: 'error',
                    })
                    break
                case 'exists_email':
                    showToast(t`You're already a member. Please login`, {
                        level: 'error',
                    })
                    break
                case 'invalid_credentials':
                    showToast(t`Wrong user credentials`, {
                        level: 'error',
                    })
                    break
                case 'not_verified':
                    navigate('/register/user/verification', {replace: true})
                    return
                case 'signup_not_allowed':
                    navigate('/register/user/not-allowed', {replace: true})
                    return
                case 'invalid_state':
                    showAlertModal({
                        title: t`An error occurred`,
                        message: t`An error occurred. Please try again`,
                    })
                    break
                default:
                    break
            }

            navigate(location.pathname, {replace: true})
        }

        // If there's a email and idToken within the URL hash params:
        // These values originates from a SAML callback and we should refresh the session
        // with the provided idToken.
        if (oauthParams?.email && oauthParams?.idToken) {
            forceSessionRefresh(oauthParams?.idToken)
            navigate('/trust-browser', {
                state: {addAccount: false, email: oauthParams?.email},
            })
        }

        // This is condition is made to Google Signup Intergration when it returned `email`, `firstName` and `lastName`
        // If it's the registration flow, it should not have a loginToken nor idToken
        if (oauthParams?.email && !oauthParams.loginToken && !oauthParams?.idToken) {
            setParams({
                ...params,
                email: oauthParams?.email,
                firstName: oauthParams?.firstName || undefined,
                lastName: oauthParams?.lastName || undefined,
                activeRegisterType: 'admin',
            })
            tracking.createAccountActioned({
                action: 'completed',
                signin: 'google',
            })
            navigate(location.pathname, {replace: true})
        }

        if (isOauthAddAccount()) {
            setOauthAddAccount(false)

            if (oauthParams?.loginToken) {
                navigate('/add-account', {state: {...oauthParams}, replace: true})
            }
        }

        // If the initial page url contains oAuth params in hash, we should be safe to remove them
        // at this point, since the data is already used for error feedback or initial session state
        // in Session Provider
        if (oauthParams?.accessToken) {
            navigate(location.pathname, {replace: true})
        }
    }, [location.pathname])
}

// We're setting this flag when trying to add a new account via
// "login with google" button so we can do proper redirect
// when we get back from the google oauth url
export function setOauthAddAccount(addAccount: boolean) {
    if (addAccount) {
        localStorage.setItem(OAUTH_ADD_ACCOUNT, 'true')
    } else {
        localStorage.removeItem(OAUTH_ADD_ACCOUNT)
    }
}

function isOauthAddAccount() {
    return !!localStorage.getItem(OAUTH_ADD_ACCOUNT)
}

// #endregion OAuth URL parsing
////////////////////////////////////////////////////////////////////////////////
